import { useMemo, type ReactNode } from 'react'
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from '@dnd-kit/core'
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable'

// Hoisted so useSensor's internal memo (keyed on the options reference) holds.
const POINTER_OPTIONS = { activationConstraint: { distance: 6 } }

// Ленты горизонтальные — элемент ездит только по X. Без этого перетаскиваемый
// урок/экран уходил за курсором вверх-вниз, а автоскролл dnd-kit прокручивал по
// вертикали всё, что прокручивается вокруг: поле уезжало, соседние уроки
// пропадали из вида (жалоба с беты). Автоскролл — тоже только по X: у края
// длинной ленты она по-прежнему листается сама.
const lockToX: Modifier = ({ transform }) => ({ ...transform, y: 0 })
const MODIFIERS = [lockToX]
const AUTO_SCROLL = { threshold: { x: 0.2, y: 0 } }

interface Props {
  items: ReadonlyArray<{ id: number }>
  onReorder: (fromIndex: number, toIndex: number) => void
  children: ReactNode
}

// Shared shell for horizontal drag-to-reorder strips (blocks, lesson pills).
// Children are expected to be SortableItem elements matching `items` by id.
export const SortableStrip = ({ items, onReorder, children }: Props) => {
  const sensors = useSensors(useSensor(PointerSensor, POINTER_OPTIONS))
  const itemIds = useMemo(() => items.map((i) => i.id), [items])

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return
    const fromIndex = items.findIndex((i) => i.id === active.id)
    const toIndex = items.findIndex((i) => i.id === over.id)
    if (fromIndex < 0 || toIndex < 0) return
    onReorder(fromIndex, toIndex)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={MODIFIERS}
      autoScroll={AUTO_SCROLL}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={itemIds} strategy={horizontalListSortingStrategy}>
        {children}
      </SortableContext>
    </DndContext>
  )
}
